import React, { useEffect, useCallback } from 'react';
import { debug } from '@/utils/debug';
import { formatAccountId } from '@/utils/googleAds';
import styles from './PollingStates.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface HealthCheckErrorProps {
  customerId: string;
  healthLevel: string;
  message: string;
  onRetry: () => void;
  onClose: () => void;
}

export function HealthCheckError({
  customerId,
  healthLevel,
  message,
  onRetry,
  onClose,
}: HealthCheckErrorProps) {
  debug.log('HealthCheckError: Rendering health check error', {
    customerId,
    healthLevel,
  });

  const handleRetry = () => {
    debug.log('HealthCheckError: Retry with different account requested');
    onRetry();
  };

  const handleClose = () => {
    debug.log('HealthCheckError: Close requested');
    onClose();
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const getHealthIcon = () => {
    switch (healthLevel) {
      case 'suspended': return '🚫';
      case 'critical': return '⛔';
      case 'warning': return '⚠️';
      default: return '❌';
    }
  };

  const getHealthColor = () => {
    switch (healthLevel) {
      case 'suspended': return '#7f1d1d';  // Dark red
      case 'critical': return '#dc2626';   // Red
      case 'warning': return '#d97706';    // Amber
      default: return '#6b7280';           // Gray
    }
  };

  const modalTitleId = 'health-check-error-title';

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>Account Health Check Failed</h3>
          <button
            className={modalStyles.modalClose}
            onClick={handleClose}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            <div className={styles.header}>
              <div
                className={styles.icon}
                style={{ color: getHealthColor() }}
              >
                {getHealthIcon()}
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.message}>
                <p className={styles.primaryText}>
                  {healthLevel === 'suspended'
                    ? 'This Google Ads account cannot be linked.'
                    : 'We cannot link this Google Ads account due to policy issues.'}
                </p>
                <p className={styles.secondaryText}>
                  <strong>Customer ID:</strong> {formatAccountId(customerId)}
                </p>
              </div>

              <div className={`${styles.infoSection} ${styles.helpSection}`}>
                <h4 className={styles.sectionTitle}>How to Fix</h4>
                {healthLevel === 'suspended' ? (
                  <ul className={styles.stepsList}>
                    <li>
                      Log in to your Google Ads account at{' '}
                      <a
                        href="https://ads.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb' }}
                      >
                        ads.google.com
                      </a>
                    </li>
                    <li>If your account is <strong>new</strong>, complete the account setup process</li>
                    <li>If your account was <strong>deactivated</strong>, reactivate it in your account settings</li>
                    <li>If your account is <strong>suspended</strong>, review the suspension notice and submit an appeal</li>
                    <li>Return here to try linking again once your account is accessible</li>
                  </ul>
                ) : (
                  <ul className={styles.stepsList}>
                    <li>
                      Log in to your Google Ads account at{' '}
                      <a
                        href="https://ads.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb' }}
                      >
                        ads.google.com
                      </a>
                    </li>
                    <li>Navigate to "Ads & extensions" to review disapproved ads</li>
                    <li>Fix or remove ads with policy violations</li>
                    <li>Wait for Google to re-review your ads (usually 1 business day)</li>
                    <li>Return here to try linking again once issues are resolved</li>
                  </ul>
                )}
              </div>

              <div className={styles.actions}>
                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleRetry}
                >
                  Try Different Account
                </button>
                <button
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>

              <div className={`${styles.help} ${styles.errorHelp}`}>
                <p className={styles.helpText}>
                  <strong>Need help?</strong> If you believe this is an error, contact{' '}
                  <a href="mailto:support@flouri.ai" style={{ color: '#2563eb' }}>support@flouri.ai</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
